import { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { DefaultAndValue } from "../util/restore";
import { EditorHeaderWithPresetChooser } from "./EditorHeaderWithPresetChooser";


interface PresetTextEditorProps {
  requestFocus?: boolean;
  isTwoLetter?: boolean;
  label?: string;
  value: DefaultAndValue;
  onChange: (newValue: DefaultAndValue) => void;
  onEnter?: () => void;
}

export function PresetTextEditor(props: PresetTextEditorProps) {
  return GenericTextEditor(props);
}

interface TextEditorProps {
  requestFocus?: boolean;
  isTwoLetter?: boolean;
  label?: string;
  value: string;
  onChange: (newValue: string) => void;
  onEnter?: () => void;
}

export function TextEditor(props: TextEditorProps) {
  return GenericTextEditor(props);
}

interface GenericTextEditorProps {
  requestFocus?: boolean;
  isTwoLetter?: boolean;
  label?: string;
  value: string | DefaultAndValue;
  onChange: (newValue: string | DefaultAndValue) => void;
  onEnter?: () => void;
}

function GenericTextEditor(props: GenericTextEditorProps) {
  const { isTwoLetter, label, value, onChange } = props;
  const inputRef = useRef(null);

  const isValueWithoutDefault = (typeof value === 'string');

  useLayoutEffect(() => {
    if (inputRef.current && props.requestFocus) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const maybeConvertChange = (v) => {
    if (isValueWithoutDefault) {
      onChange(v)
    } else {
      onChange({
        default: value.default,
        value: v
      })
    }
  } 

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isTwoLetter) {
      maybeConvertChange(e.target.value)
      return;
    }
    const newValue = e.target.value.toUpperCase(); // Convert to uppercase for consistency
    if (newValue.length <= 2 && /^[A-Z]*$/.test(newValue)) {
      maybeConvertChange(newValue);
    }
  };

  const inputValue = isValueWithoutDefault ? value : value["value"];

  return (
    <Container>
      <EditorHeaderWithPresetChooser 
        label={label}
        value={value}
        onChange={maybeConvertChange}
      />
      <Input
        ref={inputRef}
        $isTwoLetter={isTwoLetter}
        type="text"
        maxLength={256}
        value={inputValue}
        onChange={(e) => handleChange(e)}
        onKeyDown={(e) => {
          if (props.onEnter && e.key === 'Enter') {
            props.onEnter()
          }          
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  /* margin-bottom: 1rem; */
  flex-grow: 1;
`;

interface InputProps {
  $isTwoLetter?: boolean;
}
const Input = styled.input<InputProps>`
  width: ${(props) => (props.$isTwoLetter ? '50px' : '100%')};
  padding: 0.5rem;
  border: 1px solid var(--border-color); 
  border-radius: 0.25rem;
  text-align: ${(props) => (props.$isTwoLetter ? 'center' : 'left')};
`;
